import { Show } from "solid-js";

import {
  useAccount,
  useContact,
  useCustomRecord,
  useDeal,
  useProduct,
  useWorkspaceUser,
} from "~/lib/hooks";
import { formatUserName } from "~/lib/types";
import type { CustomFieldLookupTarget } from "~/lib/types";

type Props = {
  wsKey: () => string;
  target: CustomFieldLookupTarget;
  // Required when target='custom_record'; ignored otherwise.
  customObjectDefinitionId?: string | null;
  // The lookup value — a TypedID string (e.g. `acc_…`). When empty,
  // this component falls through to a dim "—" placeholder so the parent
  // doesn't have to special-case.
  value: string | null | undefined;
};

/**
 * Display name for a single lookup-field value. Resolves the entity by ID
 * via the existing TanStack Query hooks so the cache deduplicates fetches
 * across multiple lookup fields pointing at the same entity.
 *
 * Each branch instantiates exactly one hook because `target` is fixed for
 * the lifetime of the chip — switching to a different lookup target means
 * a different chip is rendered.
 */
export function LookupValue(props: Props) {
  return (
    <>
      <Show when={!props.value}>
        <span class="opacity-50">—</span>
      </Show>
      <Show when={props.value && props.target === "account"}>
        <AccountName wsKey={props.wsKey} value={props.value as string} />
      </Show>
      <Show when={props.value && props.target === "contact"}>
        <ContactName wsKey={props.wsKey} value={props.value as string} />
      </Show>
      <Show when={props.value && props.target === "user"}>
        <UserName wsKey={props.wsKey} value={props.value as string} />
      </Show>
      <Show when={props.value && props.target === "product"}>
        <ProductName wsKey={props.wsKey} value={props.value as string} />
      </Show>
      <Show when={props.value && props.target === "deal"}>
        <DealName wsKey={props.wsKey} value={props.value as string} />
      </Show>
      <Show
        when={props.value && props.target === "custom_record" && props.customObjectDefinitionId}
      >
        <CustomRecordName
          wsKey={props.wsKey}
          customObjectDefinitionId={props.customObjectDefinitionId as string}
          value={props.value as string}
        />
      </Show>
    </>
  );
}

function AccountName(props: { wsKey: () => string; value: string }) {
  const q = useAccount(props.wsKey, () => props.value);
  return <span>{q.data?.name ?? props.value}</span>;
}

function ContactName(props: { wsKey: () => string; value: string }) {
  const q = useContact(props.wsKey, () => props.value);
  return <span>{q.data?.full_name ?? props.value}</span>;
}

function UserName(props: { wsKey: () => string; value: string }) {
  const q = useWorkspaceUser(props.wsKey, () => props.value);
  return <span>{q.data ? formatUserName(q.data) : props.value}</span>;
}

function ProductName(props: { wsKey: () => string; value: string }) {
  const q = useProduct(props.wsKey, () => props.value);
  return <span>{q.data?.name ?? props.value}</span>;
}

function DealName(props: { wsKey: () => string; value: string }) {
  const q = useDeal(props.wsKey, () => props.value);
  return <span>{q.data?.name ?? props.value}</span>;
}

function CustomRecordName(props: {
  wsKey: () => string;
  customObjectDefinitionId: string;
  value: string;
}) {
  const q = useCustomRecord(
    props.wsKey,
    () => props.customObjectDefinitionId,
    () => props.value,
  );
  return <span>{q.data?.name ?? props.value}</span>;
}
